import React, { ReactNode, useState, ReactElement, useEffect } from 'react';
import AccordianMinusIcon from '../../assets/AccordianMinusIcon';
import AccordianPlusIcon from '../../assets/AccordianPlusIcon';
import AccordianMinusIconDark from '../../assets/icon/AccordianMinusIconDark';
import AccordianPlusIconDark from '../../assets/icon/AccordianPlusIconDark';

interface AccordionProps {
  title: string;
  children: ReactNode;
  isOpenDefault?: boolean;
  iconClose?: ReactElement;
  iconOpen?: ReactElement;
  icon?: ReactElement;
  bgColor?: string;
  status?: string;
  formComponent?: ReactElement;
  closeModal?: () => void;
  onToggleEditMode?: (bookingId: number, bookingProductTripId: number) => void;
  bookingId?: number;
  bookingProductTripId?: number;
  onClose?: () => void;
  onOpen?: () => void;
  accordionIsOpen?: boolean;
  toggleState?: (isOpen: boolean) => void;
  isActive?: boolean;
  isFlightCancelled?: string;
  isNoInformation?: boolean;
}

const Accordian: React.FC<AccordionProps> = ({
  title,
  children,
  isOpenDefault = true,
  icon,
  bgColor,
  iconClose,
  iconOpen,
  status,
  formComponent,
  closeModal,
  onToggleEditMode,
  bookingId,
  bookingProductTripId,
  accordionIsOpen,
  toggleState,
  isActive,
  isFlightCancelled,
  isNoInformation,
}) => {
  const [isOpen, setIsOpen] = useState(accordionIsOpen);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (accordionIsOpen !== undefined) {
      setIsOpen(accordionIsOpen);
    }
  }, [accordionIsOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (toggleState) {
      toggleState(isOpen ?? false); // Ensure a boolean value is passed
    }
    if (closeModal) {
      closeModal();
    }
  };

  const handleAccordionToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (toggleState) {
      toggleState(newState); // Inform the parent of the updated state
    }
  };

  return (
    <div className={`rounded mb-4 ${bgColor}`}>
      <button
        onClick={handleAccordionToggle}
        className="w-full text-left flex justify-between items-center p-4 text-textColor text-sm font-semibold"
      >
        {/* Arrow Icon Moved to Left */}
        <span className="flex items-center gap-2">
          <span>
            {isOpen ? (
              iconClose ? (
                iconClose
              ) : (
                <div>
                  <div className="block dark:hidden">
                    <AccordianMinusIcon />
                  </div>
                  <div className="hidden dark:block">
                    <AccordianMinusIconDark />
                  </div>
                </div>
              )
            ) : iconOpen ? (
              iconOpen
            ) : (
              <div>
                <div className="block dark:hidden">
                  <AccordianPlusIcon />
                </div>
                <div className="hidden dark:block">
                  <AccordianPlusIconDark />
                </div>
              </div>
            )}
          </span>
          <span className="ml-2 custom-word-break">{title}</span>
        </span>

        <div className="flex items-center gap-4">
          <span className="ml-2 flex items-center gap-2">
            {isActive && (
              <span
                className={`w-full h-[30px] px-3 py-1 rounded-full text-sm text-center font-medium ${
                  isFlightCancelled === 'Active'
                    ? 'bg-green-100 text-green'
                    : isFlightCancelled !== ''
                      ? 'bg-red-100 text-red-700'
                      : ''
                }`}
              >
                {isFlightCancelled}
              </span>
            )}
          </span>

          <span className="ml-2 flex items-center gap-2">
            {status && (
              <span
                className={`w-[130px] h-[30px] px-3 py-1 rounded-full text-sm text-center font-medium 
                `}
              >
                {status}
              </span>
            )}
          </span>

          <span>
            {icon && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModalOpen(true);
                  onToggleEditMode &&
                    bookingId &&
                    bookingProductTripId &&
                    onToggleEditMode(bookingId, bookingProductTripId);
                }}
              >
                {icon}
              </span>
            )}
          </span>
        </div>
      </button>
      {isOpen && (
        <div
          className={`p-4 border-t-2 rounded-b-md border-customBorder-tableBorder ${bgColor}`}
        >
          {children}
        </div>
      )}

      {/* Render modal only when isModalOpen is true */}
      {isModalOpen && formComponent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 md:p-0">
          {React.cloneElement(formComponent, { closeModal: handleCloseModal })}
        </div>
      )}
    </div>
  );
};

export default Accordian;
